import { ActivityNames, CaseReport, CaseReportSegment } from './CaseTypes';
import { formatTimeDisplay, getActivityColor } from '../utils/caseUtils';
import { TimelineSegmentData } from '../components/Charts/CaseTimelineChart';

/**
 * Swim lane assignments (row index for each segment type).
 * Row 0 is at the bottom, higher numbers are higher on the chart.
 * Lanes with no segments will be hidden dynamically.
 */
const SWIM_LANE_CONFIG: Record<string, number> = {
  [ActivityNames.EXPOSURE]: 0,
  [ActivityNames.INTRAOP_MEASUREMENT]: 1,
  // Shared swim lane for cannulation-related segments
  // v1.0 has combined "Cannulation/Screw Placement"
  // v1.2 splits into "Anatomy Localization", "Cannulation", "Screw Placement"
  [ActivityNames.CANNULATION_SCREW_PLACEMENT]: 2,
  [ActivityNames.ANATOMY_LOCALIZATION]: 2,
  [ActivityNames.CANNULATION]: 2,
  [ActivityNames.SCREW_PLACEMENT]: 2,
  [ActivityNames.DECOMPRESSION_ALIGNMENT]: 3,
  [ActivityNames.POST_CANNULATION]: 4,
  [ActivityNames.CLOSURE]: 5,
};

/**
 * Adapts CaseReport segment data into TimelineSegmentData[] for the CaseTimelineChart.
 * Each segment is placed in its appropriate swim lane based on segment type.
 */
export default class TimelineAdapter {
  caseReport: CaseReport;

  constructor(caseReport: CaseReport) {
    this.caseReport = caseReport;
  }

  /**
   * Transform CaseReportSegment data into TimelineSegmentData for the CaseTimelineChart.
   * Each segment is placed in its appropriate swim lane based on segment type.
   *
   * Note: Anatomy Localization, Cannulation, and Screw Placement share the same swim lane.
   */
  getTimelineData = (): TimelineSegmentData[] => {
    if (!this.caseReport.segments || this.caseReport.segments.length === 0) {
      return [];
    }

    // Helper to get color for a segment type
    const getSegmentColor = (segmentType: string): string => {
      const color = getActivityColor(segmentType);
      if (color === 'var(--chart-5)') {
        console.warn(`[TimelineAdapter] No color found for segment type: "${segmentType}"`);
      }
      return color;
    };

    // Filter and transform segments that have a defined swim lane
    return this.caseReport.segments
      .filter((segment: CaseReportSegment) => {
        const swimLane = SWIM_LANE_CONFIG[segment.segmentType];
        if (swimLane === undefined) {
          console.warn(`[TimelineAdapter] No swim lane for segment type: "${segment.segmentType}"`);
        }
        return swimLane !== undefined;
      })
      .map((segment: CaseReportSegment): TimelineSegmentData => {
        const duration = segment.normalizedEndMin - segment.normalizedStartMin;
        return {
          segmentType: segment.segmentType,
          startMin: segment.normalizedStartMin,
          endMin: segment.normalizedEndMin,
          duration,
          displayDuration: formatTimeDisplay(duration, true),
          color: getSegmentColor(segment.segmentType),
          swimLane: SWIM_LANE_CONFIG[segment.segmentType],
          videoIsValid: segment.videoIsValid,
          videoStartSec: segment.videoStartSec,
          videoEndSec: segment.videoEndSec,
          signedUrl: segment.signedUrl,
        };
      })
      .sort((a, b) => a.startMin - b.startMin);
  };

  /**
   * Format the total navigated time for display in the chart header.
   */
  formatTotalNavigatedTime = (): string => {
    const navigatedTime = this.caseReport.totals?.total_navigated_time ?? 0;
    return formatTimeDisplay(navigatedTime, true);
  };
}
